<template>
  <item-group
    mandatory
    :model-value="currentTool"
    @update:model-value="setCurrentTool($event)"
  >
    <div class="my-1 tool-separator" />
    <groupable-item
      v-slot:default="{ active, toggle }"
      :value="Tools.WindowLevel"
    >
      <tool-button
        icon="mdi-circle-half-full"
        name="Window & Level"
        :buttonClass="['tool-btn', active ? 'tool-btn-selected' : '']"
        :disabled="noCurrentImage"
        @click="toggle"
      />
    </groupable-item>
    <groupable-item v-slot:default="{ active, toggle }" :value="Tools.Pan">
      <tool-button
        icon="mdi-cursor-move"
        name="Pan"
        :buttonClass="['tool-btn', active ? 'tool-btn-selected' : '']"
        :disabled="noCurrentImage"
        @click="toggle"
      />
    </groupable-item>
    <groupable-item v-slot:default="{ active, toggle }" :value="Tools.Zoom">
      <tool-button
        icon="mdi-magnify-plus-outline"
        name="Zoom"
        :buttonClass="['tool-btn', active ? 'tool-btn-selected' : '']"
        :disabled="noCurrentImage"
        @click="toggle"
      />
    </groupable-item>
    <groupable-item
      v-slot:default="{ active, toggle }"
      :value="Tools.Crosshairs"
    >
      <tool-button
        icon="mdi-crosshairs"
        name="Crosshairs"
        :buttonClass="['tool-btn', active ? 'tool-btn-selected' : '']"
        :disabled="noCurrentImage"
        @click="toggle"
      />
    </groupable-item>
    <div class="my-1 tool-separator" />
    <groupable-item v-slot:default="{ active, toggle }" :value="Tools.Paint">
      <menu-tool-button
        icon="mdi-brush"
        name="Paint"
        :active="active"
        :disabled="noCurrentImage"
        @click="toggle"
      >
        <paint-controls />
      </menu-tool-button>
    </groupable-item>
    <groupable-item
      v-slot:default="{ active, toggle }"
      :value="Tools.Rectangle"
    >
      <menu-tool-button
        icon="mdi-vector-square"
        name="Rectangle"
        :mobileOnlyMenu="true"
        :active="active"
        :disabled="noCurrentImage"
        @click="toggle"
      >
        <rectangle-controls />
      </menu-tool-button>
    </groupable-item>
    <groupable-item v-slot:default="{ active, toggle }" :value="Tools.Ruler">
      <menu-tool-button
        icon="mdi-ruler"
        name="Ruler"
        :mobileOnlyMenu="true"
        :active="active"
        :disabled="noCurrentImage"
        @click="toggle"
      >
        <ruler-controls />
      </menu-tool-button>
    </groupable-item>

    <div class="my-1 tool-separator" />
    <groupable-item v-slot:default="{ active, toggle }" :value="Tools.Crop">
      <menu-tool-button
        icon="mdi-crop"
        name="Crop"
        :active="active"
        :disabled="noCurrentImage"
        @click="toggle"
      >
        <crop-controls />
      </menu-tool-button>
    </groupable-item>
    <div class="my-1 tool-separator" />
    <groupable-item v-slot:default="{ active, toggle }" :value="Tools.Landmark3D">
      <menu-tool-button
        icon="mdi-circle"
        name="Click Landmark"
        :active="active"
        :disabled="noCurrentModel"
        @click="toggle"
      />
    </groupable-item>
    <groupable-item v-slot:default="{ active, toggle }" :value="Tools.SurfaceDrawing">
      <menu-tool-button
        icon="mdi-brush"
        name="Label Surface"
        :active="active"
        :disabled="noCurrentModel"
        @click="toggle"
      >
      <surface-drawing-controls />
      </menu-tool-button>
    </groupable-item>
    <groupable-item
      v-slot:default="{ active, toggle }"
      :value="Tools.Spline"
    >
      <menu-tool-button
        icon="mdi-vector-radius"
        name="Add Measurement"
        :mobileOnlyMenu="true"
        :active="active"
        :disabled="noCurrentModel"
        @click="toggle"
      >
      </menu-tool-button>
    </groupable-item>
  </item-group>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue';
import { onKeyDown } from '@vueuse/core';
import { Tools } from '@/src/store/tools/types';
import ToolButton from './ToolButton.vue';
import ItemGroup from './ItemGroup.vue';
import GroupableItem from './GroupableItem.vue';
import { useDatasetStore } from '../store/datasets';
import { useToolStore } from '../store/tools';
import PaintControls from './PaintControls.vue';
import MenuToolButton from './MenuToolButton.vue';
import CropControls from './tools/crop/CropControls.vue';
import { useRectangleStore } from '../store/tools/rectangles';
import { useRulerStore } from '../store/tools/rulers';
import RulerControls from './RulerControls.vue';
import RectangleControls from './RectangleControls.vue';
import SurfaceDrawingControls from './tools/SurfaceDrawingControls.vue';
import { useModelStore } from '../store/datasets-models';

export default defineComponent({
  components: {
    ToolButton,
    MenuToolButton,
    ItemGroup,
    SurfaceDrawingControls,
    GroupableItem,
    PaintControls,
    CropControls,
    RulerControls,
    RectangleControls,
  },
  setup() {
    const dataStore = useDatasetStore();
    const modelStore = useModelStore();
    const toolStore = useToolStore();

    const noCurrentImage = computed(() => !dataStore.primaryDataset);
    const noCurrentModel = computed(() => modelStore.idList.length == 0);
    const currentTool = computed(() => toolStore.currentTool);

    const rectangleStore = useRectangleStore();
    const rulerStore = useRulerStore();

    const paintMenu = ref(false);
    const cropMenu = ref(false);

    onKeyDown('Escape', () => {
      paintMenu.value = false;
      cropMenu.value = false;
    });

    // Add keyboard shortcut for crosshairs
    onKeyDown('c', (e) => {
      // Only trigger if not in an input field
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }
      
      if (!noCurrentImage.value) {
        const newTool = toolStore.currentTool === Tools.Crosshairs ? null : Tools.Crosshairs;
        toolStore.setCurrentTool(newTool);
      }
    });

    return {
      currentTool,
      setCurrentTool: toolStore.setCurrentTool,
      noCurrentImage,
      noCurrentModel,
      Tools,
      paintMenu,
      cropMenu,
      rectangleStore,
      rulerStore,
    };
  },
});
</script>

<style>
.tool-btn-selected {
  background-color: rgb(var(--v-theme-selection-bg-color));
}
</style>

<style scoped>
.menu-more {
  position: absolute;
  right: -10%;
}

.tool-separator {
  width: 75%;
  height: 1px;
  border: none;
  border-top: 1px solid rgb(112, 112, 112);
}

.popup-menu {
  max-width: 400px; /* a little less than v-navigation-drawer in App.vue */
}
</style>
